/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Current Subscription & Quota Card Component (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  CreditCard,
  Calendar,
  ExternalLink,
  Sparkles,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSubscriptionStore } from '../../stores/useSubscriptionStore';

export const CurrentSubscriptionCard: React.FC = () => {
  const { t } = useTranslation();
  const {
    subscription,
    plans,
    getSubscriptionUsagePercent,
    openStripeCustomerPortal,
    setCancelModalOpen,
    resumeSubscription,
    isCancelModalOpen,
    cancelSubscription,
    isCustomerPortalLoading,
  } = useSubscriptionStore();

  const currentPlan = plans.find((p) => p.id === subscription.planId) || plans[1];
  const usagePercent = getSubscriptionUsagePercent();
  const remainingCredits = Math.max(0, subscription.monthlyCredits - subscription.creditsUsedThisPeriod);

  const renewalDate = new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface border border-surface-border p-6 shadow-xs transition-all">
        {/* Ambient Top Glow */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Plan Details & Status */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-primary/15 text-primary border border-primary/20">
                {t('subscription.currentPlan')}
              </span>

              {subscription.cancelAtPeriodEnd ? (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25">
                  <AlertTriangle className="size-3" />
                  <span>{t('subscription.canceledStatus')}</span>
                </span>
              ) : (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                  <CheckCircle2 className="size-3" />
                  <span>{t('subscription.activeStatus')}</span>
                </span>
              )}
            </div>

            <div className="space-y-1">
              <h2 className="text-2xl font-bold font-heading text-foreground flex items-center gap-2">
                <span>{currentPlan.name}</span>
                <span className="text-sm font-normal text-foreground-muted">
                  (${currentPlan.monthlyPrice}{t('subscription.perMonth')})
                </span>
              </h2>
              <p className="text-xs text-foreground-muted flex items-center gap-1.5">
                <Calendar className="size-3.5 text-foreground-muted" />
                <span>
                  {subscription.cancelAtPeriodEnd
                    ? t('subscription.expiresOn', { date: renewalDate })
                    : t('subscription.renewsOn', { date: renewalDate })}
                </span>
              </p>
            </div>

            {/* Quota Progress Bar */}
            <div className="pt-2 max-w-xl space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-foreground">
                  {t('subscription.periodUsage')}
                </span>
                <span className="text-foreground-muted font-mono">
                  {t('subscription.creditsUsed', {
                    used: subscription.creditsUsedThisPeriod.toLocaleString(),
                    total: subscription.monthlyCredits.toLocaleString(),
                    percent: usagePercent,
                  })}
                </span>
              </div>

              <div className="h-2.5 w-full rounded-full bg-muted/70 overflow-hidden p-0.5 border border-surface-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                  style={{ width: `${usagePercent}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-[11px] text-foreground-muted">
                <span className="flex items-center gap-1">
                  <Sparkles className="size-3 text-primary" />
                  {t('subscription.creditsRemaining', { remaining: remainingCredits.toLocaleString() })}
                </span>
                <span className="font-mono">
                  {subscription.paymentMethodPreview.brand} •••• {subscription.paymentMethodPreview.last4}
                </span>
              </div>
            </div>
          </div>

          {/* Right Action Buttons */}
          <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end gap-3 flex-shrink-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-surface-border">
            <button
              type="button"
              onClick={openStripeCustomerPortal}
              disabled={isCustomerPortalLoading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-primary-fg text-xs font-semibold shadow-xs hover:shadow-md transition-all active:scale-95 disabled:opacity-50"
            >
              <CreditCard className="size-4" />
              <span>
                {isCustomerPortalLoading ? t('subscription.portalLoading') : t('subscription.manageBilling')}
              </span>
              <ExternalLink className="size-3.5 opacity-70" />
            </button>

            {subscription.cancelAtPeriodEnd ? (
              <button
                type="button"
                onClick={resumeSubscription}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface hover:bg-surface-hover text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-medium transition-colors"
              >
                <RotateCcw className="size-3.5" />
                <span>{t('subscription.resumePlan')}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCancelModalOpen(true)}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs text-foreground-muted hover:text-rose-500 transition-colors"
              >
                <span>{t('subscription.cancelPlan')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Confirmation Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="w-full max-w-md p-6 rounded-2xl bg-surface border border-surface-border shadow-xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-rose-500/10 text-rose-500 border border-rose-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="size-5" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-bold font-heading text-foreground">
                  {t('subscription.cancelConfirmTitle')}
                </h3>
                <p className="text-xs text-foreground-muted leading-relaxed">
                  {t('subscription.cancelConfirmDesc', { date: renewalDate })}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setCancelModalOpen(false)}
                className="px-4 py-2 rounded-xl bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-xs font-medium transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={cancelSubscription}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-xs transition-colors"
              >
                {t('subscription.cancelConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
